import React from 'react';
import { ArrowRightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Card, Typography } from 'antd';

const { Text } = Typography;

export const DetailField: React.FC<{
  label: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}> = ({ label, children, className = '' }) => {
  const value = children == null || children === '' ? '—' : children;

  return (
    <div className={`transport-detail-field ${className}`.trim()}>
      <Text type="secondary" className="transport-detail-field__label">{label}</Text>
      <div className="transport-detail-field__value">{value}</div>
    </div>
  );
};

export const AddressList: React.FC<{ addresses: string[] }> = ({ addresses }) => {
  const visibleAddresses = addresses.filter((address) => address.trim().length > 0);

  if (visibleAddresses.length === 0) {
    return <span>—</span>;
  }

  return (
    <ol className="transport-detail-address-list">
      {visibleAddresses.map((address, index) => (
        <li key={`${address}-${index}`}>{address}</li>
      ))}
    </ol>
  );
};

interface RouteJourneyProps {
  loadingLabel: React.ReactNode;
  deliveryLabel: React.ReactNode;
  loadingAddresses: string[];
  deliveryAddresses: string[];
  dates: Array<{ label: React.ReactNode; value: React.ReactNode }>;
  extra?: React.ReactNode;
}

export const RouteJourney: React.FC<RouteJourneyProps> = ({
  loadingLabel,
  deliveryLabel,
  loadingAddresses,
  deliveryAddresses,
  dates,
  extra,
}) => (
  <div className="transport-detail-journey" data-testid="transport-detail-journey">
    <div className="transport-detail-journey__stops">
      <div className="transport-detail-stop">
        <span className="transport-detail-stop__icon"><EnvironmentOutlined /></span>
        <DetailField label={loadingLabel}>
          <AddressList addresses={loadingAddresses} />
        </DetailField>
      </div>
      <div className="transport-detail-journey__line" aria-hidden="true">
        <ArrowRightOutlined />
      </div>
      <div className="transport-detail-stop transport-detail-stop--delivery">
        <span className="transport-detail-stop__icon"><EnvironmentOutlined /></span>
        <DetailField label={deliveryLabel}>
          <AddressList addresses={deliveryAddresses} />
        </DetailField>
      </div>
    </div>
    <div className="transport-detail-date-grid">
      {dates.map((date, index) => (
        <DetailField key={index} label={date.label}>{date.value}</DetailField>
      ))}
      {extra}
    </div>
  </div>
);

export const MetricTile: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  emphasis?: boolean;
}> = ({ label, value, emphasis = false }) => (
  <div className={`transport-detail-metric${emphasis ? ' transport-detail-metric--emphasis' : ''}`}>
    <Text type="secondary" className="transport-detail-metric__label">{label}</Text>
    <div className="transport-detail-metric__value">{value}</div>
  </div>
);

export const DetailSection: React.FC<{
  title: React.ReactNode;
  children: React.ReactNode;
  extra?: React.ReactNode;
  className?: string;
}> = ({ title, children, extra, className = '' }) => (
  <Card
    title={title}
    extra={extra}
    className={`transport-detail-section ${className}`.trim()}
  >
    {children}
  </Card>
);

export const ValueGrid: React.FC<{
  children: React.ReactNode;
  columns?: 2 | 3 | 4 | 5;
  className?: string;
}> = ({ children, columns = 2, className = '' }) => (
  <div
    className={`transport-detail-value-grid transport-detail-value-grid--${columns} ${className}`.trim()}
  >
    {children}
  </div>
);

